import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatusPill, Loader } from "@/components/ui-bits";
import { ArrowDownToLine, Check, X, Copy, CheckCheck, Wallet } from "lucide-react";

const CopyAddress = ({ address }) => {
  const [copied, setCopied] = useState(false);
  if (!address) return <span className="text-xs text-slate-500">No BEP20 address</span>;
  const short = address.length > 16 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;
  const copy = async () => {
    try { await navigator.clipboard.writeText(address); } catch { /* ignore */ }
    setCopied(true); toast.success("BEP20 address copied");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="font-num text-xs text-emerald-300" title={address}>{short}</span>
      <button data-testid="wd-copy-addr" onClick={copy}
        className="shrink-0 inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-1 rounded-lg bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25">
        {copied ? <CheckCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

export default function AdminWithdrawals() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = () => API.get("/admin/withdrawals").then((r) => setItems(r.data.withdrawals));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { const r = await API.post(`/admin/withdrawals/${id}/approve`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    const note = window.prompt("Rejection note (optional):") || "";
    try { const r = await API.post(`/admin/withdrawals/${id}/reject`, { note }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader label="Loading withdrawals" />;
  const filtered = filter === "all" ? items : items.filter((w) => w.status === filter);

  return (
    <div>
      <PageHeader title="Withdrawals" subtitle="User withdrawal requests with their BEP20 payout address. Approve or reject each request." testid="admin-withdrawals-header" />

      <div className="flex gap-2 mb-6">
        {["pending", "approved", "rejected", "all"].map((t) => (
          <button key={t} data-testid={`wd-filter-${t}`} onClick={() => setFilter(t)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-colors ${
              filter === t ? "bg-emerald-500 text-slate-900" : "bg-white/5 text-slate-400 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      <div className="glass rounded-2xl p-2 sm:p-4" data-testid="admin-withdrawals-table">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500 py-12 text-center">No {filter === "all" ? "" : filter} withdrawals.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-3 px-3">User</th>
                <th className="py-3 px-3">Amount</th>
                <th className="py-3 px-3">Fee</th>
                <th className="py-3 px-3">Net Payout</th>
                <th className="py-3 px-3"><span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> BEP20 Address</span></th>
                <th className="py-3 px-3">Date</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {filtered.map((w) => (
                  <tr key={w.id} className="border-b border-white/5 last:border-0" data-testid={`wd-row-${w.id}`}>
                    <td className="py-3 px-3">
                      <div className="text-white">{w.user_name}</div>
                      <div className="text-xs text-slate-500">{w.user_email}</div>
                      {w.referral_code && <div className="text-[0.65rem] font-num text-cyan-300">{w.referral_code}</div>}
                    </td>
                    <td className="py-3 px-3 font-num text-white">{fmt(w.amount)}</td>
                    <td className="py-3 px-3 font-num text-rose-400">{fmt(w.fee)}</td>
                    <td className="py-3 px-3 font-num text-emerald-300">{fmt(w.net)}</td>
                    <td className="py-3 px-3"><CopyAddress address={w.bep20_address} /></td>
                    <td className="py-3 px-3 font-num text-slate-400">{new Date(w.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-3"><StatusPill status={w.status} /></td>
                    <td className="py-3 px-3 text-right">
                      {w.status === "pending" ? (
                        <div className="flex gap-2 justify-end">
                          <button data-testid={`wd-approve-${w.id}`} onClick={() => approve(w.id)}
                            className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 px-3 py-1.5 rounded-lg text-xs font-semibold"><Check className="h-3.5 w-3.5" /> Approve</button>
                          <button data-testid={`wd-reject-${w.id}`} onClick={() => reject(w.id)}
                            className="inline-flex items-center gap-1 bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 px-3 py-1.5 rounded-lg text-xs font-semibold"><X className="h-3.5 w-3.5" /> Reject</button>
                        </div>
                      ) : <span className="text-xs text-slate-500">Processed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
